import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useList } from "@/hooks/useList";
import { api, formatError } from "@/lib/api";
import { STAGE_LABELS } from "@/pages/contracts/OnboardingTab";

const APPLIES = { always: "Always", lafha_eligible: "LAFHA eligible", pte_company: "PTE company", vehicle_lease: "Vehicle lease" };

function Sel({ value, onChange, options, testId, className = "w-36" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-8 ${className}`} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function ChecklistItemsTab() {
  const list = useList("/checklist-items", { pageSize: 200 });
  const [form, setForm] = useState({ code: "", label: "", stage: "new_join", applies_when: "always", required: true, sort_order: 500 });
  const save = async (item, patch) => {
    try { await api.put(`/checklist-items/${item.id}`, patch); list.reload(); } catch (e) { toast.error(formatError(e)); }
  };
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/checklist-items", form); toast.success("Checklist item added — it will be added to matching contracts as they change"); setForm({ ...form, code: "", label: "" }); list.reload(); } catch (err) { toast.error(formatError(err)); }
  };
  const remove = async (item) => {
    try { await api.delete(`/checklist-items/${item.id}`); toast.success("Deleted"); list.reload(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="checklist-items-tab">
      <form onSubmit={add} className="flex flex-wrap gap-2 items-center bg-white border border-slate-200 rounded-lg p-3">
        <Input required placeholder="code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="h-8 w-36 font-mono" data-testid="checklist-new-code" />
        <Input required placeholder="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="h-8 flex-1 min-w-[200px]" data-testid="checklist-new-label" />
        <Sel value={form.stage} onChange={(v) => setForm({ ...form, stage: v })} options={STAGE_LABELS} testId="checklist-new-stage" />
        <Sel value={form.applies_when} onChange={(v) => setForm({ ...form, applies_when: v })} options={APPLIES} testId="checklist-new-applies" />
        <Button type="submit" size="sm" data-testid="checklist-new-add">Add item</Button>
      </form>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 text-left">{["Order", "Label", "Code", "Stage", "Applies when", "Required", "Active", ""].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {list.items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`checklist-item-row-${it.code}`}>
                <td className="px-3 py-2"><Input defaultValue={it.sort_order} className="h-8 w-16 font-mono" onBlur={(e) => Number(e.target.value) !== it.sort_order && save(it, { sort_order: Number(e.target.value) })} /></td>
                <td className="px-3 py-2"><Input defaultValue={it.label} className="h-8 min-w-[240px]" onBlur={(e) => e.target.value !== it.label && save(it, { label: e.target.value })} data-testid={`checklist-label-${it.code}`} /></td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{it.code}</td>
                <td className="px-3 py-2"><Sel value={it.stage} onChange={(v) => save(it, { stage: v })} options={STAGE_LABELS} className="w-32" /></td>
                <td className="px-3 py-2"><Sel value={it.applies_when} onChange={(v) => save(it, { applies_when: v })} options={APPLIES} /></td>
                <td className="px-3 py-2"><Switch checked={it.required} onCheckedChange={(v) => save(it, { required: v })} data-testid={`checklist-required-${it.code}`} /></td>
                <td className="px-3 py-2"><Switch checked={it.active} onCheckedChange={(v) => save(it, { active: v })} data-testid={`checklist-active-${it.code}`} /></td>
                <td className="px-3 py-2"><Button size="icon" variant="ghost" onClick={() => remove(it)} data-testid={`checklist-delete-${it.code}`}><Trash2 className="h-4 w-4 text-red-500" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
